import React from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
import { logger, setBaseLogContext } from "./lib/services";
import { sanitizeSentryEvent } from "./lib/services/sanitize";
import { isWebView } from "./lib/utils/webview";
import { markStart, markEnd, mark } from "./lib/utils/timing";
import { initOutboxSync } from "./lib/services/offlineOutboxService";
import { initHeartbeat } from "./lib/services/heartbeatService";
import { sentryConfig } from "./lib/config/environment";
import { clearCacheAndReload } from "./lib/utils/lazyWithRetry";
import { initYandexMetrika } from "./lib/services/yandexMetrika";

// ==================== GLOBAL CHUNK ERROR HANDLER ====================
// Handle chunk load errors globally (for errors that escape React error boundaries)
window.addEventListener('error', (event) => {
  const message = event.message || '';
  const isChunkError = 
    message.includes('Loading chunk') || 
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Failed to load');
  
  if (isChunkError) {
    const hasRetried = sessionStorage.getItem('chunk_load_retry');
    if (!hasRetried) {
      console.warn('[Global] Chunk load error detected, clearing cache and reloading...');
      sessionStorage.setItem('chunk_load_retry', 'true');
      clearCacheAndReload();
    }
  }
});

// Build ID for cache busting verification (timestamp when bundle was built)
// Exported so components can display the current version
export const BUILD_ID = typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : 'dev';

// ==================== REACT INSTANCE CHECK ====================
// Verify React is properly loaded and detect multiple instances
if (!React || !React.version) {
  console.error('[main.tsx] React is not properly loaded!');
} else {
  console.log(`[main.tsx] React version: ${React.version}`);
  
  // Check for multiple React instances (common cause of useContext null errors)
  if (import.meta.env.DEV) {
    const devTools = (window as { __REACT_DEVTOOLS_GLOBAL_HOOK__?: { renderers?: Map<unknown, unknown> } }).__REACT_DEVTOOLS_GLOBAL_HOOK__;
    if (devTools?.renderers && devTools.renderers.size > 1) {
      console.warn('[main.tsx] WARNING: Multiple React instances detected! This can cause hook errors.');
    }
  }
}

// Start timing immediately
markStart("total-cold-start");
mark("main.tsx-start");

// Detect WebView for performance optimizations
const inWebView = isWebView();

// Check if we're offline
const isOffline = !navigator.onLine;

// Offline fallback component
function OfflineFallback() {
  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div style={{
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      margin: 0,
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f766e, #14b8a6)',
      color: '#f8fafc',
      textAlign: 'center',
      padding: '32px',
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '24px',
        padding: '32px',
        maxWidth: '420px',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 20px 45px rgba(0, 0, 0, 0.25)',
      }}>
        <h1 style={{ margin: '0 0 12px', fontSize: '28px' }}>Нет соединения</h1>
        <p style={{ margin: '0 0 16px', lineHeight: 1.5, fontSize: '16px' }}>
          Вы офлайн. Проверьте сеть и повторите попытку.
        </p>
        <button
          onClick={handleRetry}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '12px',
            padding: '12px 24px',
            fontSize: '16px',
            color: '#fff',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)')}
          onMouseOut={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)')}
        >
          Повторить
        </button>
      </div>
    </div>
  );
}

// Configure Sentry with WebView-aware settings
markStart("sentry-init");

// In WebView: disable replay, reduce sampling
// In browser: full functionality
const sentryIntegrations = inWebView
  ? [
      // Minimal integrations for WebView - no replay, no console capture
      Sentry.browserTracingIntegration(),
    ]
  : [
      // Full integrations for browser
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
    ];

// Completely disable Sentry in WebView if needed for extreme performance
// Also disable when offline to prevent errors
// Also disable when DSN is not configured
const hasSentryDsn = Boolean(sentryConfig.dsn);
const shouldInitSentry = hasSentryDsn && sentryConfig.enabled && !isOffline && (!inWebView || import.meta.env.MODE !== "production");

if (shouldInitSentry) {
  try {
    Sentry.init({
      dsn: sentryConfig.dsn,
      environment: sentryConfig.environment,
      release: sentryConfig.release,
      sendDefaultPii: false,
      integrations: sentryIntegrations,
      // Reduce sampling in WebView
      tracesSampleRate: inWebView ? 0.1 : (import.meta.env.MODE === "production" ? 0.35 : 1.0),
      // Disable session replay in WebView (already excluded from integrations)
      replaysSessionSampleRate: inWebView ? 0 : 0.1,
      replaysOnErrorSampleRate: inWebView ? 0 : 1.0,
      // PII sanitization before sending to Sentry
      beforeSend: (event) => sanitizeSentryEvent(event) as Sentry.ErrorEvent | null,
    });
  } catch (error) {
    console.warn('[Sentry] Failed to initialize:', error);
  }
} else if (!hasSentryDsn) {
  console.log('[Sentry] Disabled - no DSN configured');
}

markEnd("sentry-init");
if (inWebView) {
  console.log("[Performance] WebView detected - Sentry optimized for cold start");
}

// Initialize Yandex.Metrika (production only, auto-skipped in dev/stage)
initYandexMetrika();

setBaseLogContext({ feature: "app" });

// Initialize offline outbox sync
initOutboxSync();

// Initialize heartbeat service (deferred to not block render)
if ('requestIdleCallback' in window) {
  (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(() => {
    initHeartbeat().catch(err => logger.warn('Failed to init heartbeat', { error: err }));
  });
} else {
  setTimeout(() => {
    initHeartbeat().catch(err => logger.warn('Failed to init heartbeat', { error: err }));
  }, 2000);
}

// Render app or offline fallback
mark("react-render-start");
const rootElement = document.getElementById("root")!;

// If offline and we're doing a force refresh, show offline UI
// Note: The actual app might still work from cache, but if React fails to init, show fallback
try {
  createRoot(rootElement).render(
    <React.StrictMode>
      <Sentry.ErrorBoundary
        fallback={({ error }) => {
          // If offline and got an error, show offline fallback
          if (!navigator.onLine) {
            return <OfflineFallback />;
          }
          return (
            <div className="p-6 text-center text-sm text-foreground">
              Произошла ошибка. Попробуйте обновить страницу.
            </div>
          );
        }}
      >
        <App />
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
} catch (error) {
  console.error('[main.tsx] Failed to render app:', error);
  // Fallback to simple offline UI if React fails completely
  rootElement.innerHTML = `
    <div style="font-family: system-ui; min-height: 100vh; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #0f766e, #14b8a6); color: #fff; text-align: center; padding: 32px;">
      <div style="background: rgba(255,255,255,0.1); border-radius: 24px; padding: 32px; max-width: 420px;">
        <h1 style="margin: 0 0 12px; font-size: 28px;">Ошибка загрузки</h1>
        <p style="margin: 0 0 16px;">Не удалось загрузить приложение. Проверьте соединение.</p>
        <button onclick="location.reload()" style="background: rgba(255,255,255,0.2); border: 1px solid rgba(255,255,255,0.3); border-radius: 12px; padding: 12px 24px; font-size: 16px; color: #fff; cursor: pointer;">
          Повторить
        </button>
      </div>
    </div>
  `;
}
mark("react-render-queued");

// Defer service worker registration to not block initial render
// In WebView: delay SW registration or skip entirely
if ("serviceWorker" in navigator) {
  // Prevent multiple reloads during SW update
  let isRefreshing = false;
  
  // Store registration for update checks
  let swRegistration: ServiceWorkerRegistration | null = null;
  
  // How often to check for updates (30 minutes)
  const UPDATE_CHECK_INTERVAL = 30 * 60 * 1000;
  
  // Check for SW updates
  const checkForUpdates = () => {
    if (swRegistration) {
      console.log('[SW] Checking for updates...');
      swRegistration.update().catch(err => {
        console.warn('[SW] Update check failed:', err);
      });
    }
  };
  
  const registerSW = async () => {
    try {
      // Preflight check: validate sw.js exists and is valid JS
      const swResponse = await fetch('/sw.js', { cache: 'no-store' });
      if (!swResponse.ok) {
        logger.warn('Service worker preflight failed: sw.js not found', {
          context: { feature: 'service_worker', function_name: 'preflight' },
          error_code: 'sw_preflight_not_found',
        });
        return;
      }
      
      const swText = await swResponse.text();
      // Check for raw unprocessed placeholders (old format or broken build)
      if (swText.includes('%%BUILD_VERSION%%') || swText.includes('%%PRECACHE_ONBOARDING%%')) {
        logger.warn('Service worker preflight failed: unprocessed placeholders detected', {
          context: { feature: 'service_worker', function_name: 'preflight' },
          error_code: 'sw_preflight_invalid_placeholders',
        });
        return;
      }
      
      // Log build version for debugging
      console.log(`[SW] App BUILD_ID: ${BUILD_ID}`);
      
      // Register with updateViaCache: 'none' to always check for updates
      swRegistration = await navigator.serviceWorker.register("/sw.js", {
        updateViaCache: 'none'
      });
      console.log('[SW] Registered, scope:', swRegistration.scope);
      
      // Check for updates immediately on registration
      swRegistration.update().catch(err => {
        console.warn('[SW] Update check failed:', err);
      });
      
      // Listen for new service worker installations
      swRegistration.addEventListener('updatefound', () => {
        const newWorker = swRegistration!.installing;
        if (newWorker) {
          console.log('[SW] Update found, new worker installing...');
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // There's an existing SW - activate new one immediately
                console.log('[SW] New version installed, sending SKIP_WAITING...');
                newWorker.postMessage({ type: 'SKIP_WAITING' });
              } else {
                // First install - no need to skip waiting
                console.log('[SW] First install complete');
              }
            }
          });
        }
      });
      
      // Periodic update check (every 30 minutes)
      setInterval(checkForUpdates, UPDATE_CHECK_INTERVAL);
      
      // Check for updates when tab becomes visible
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          console.log('[SW] Tab visible, checking for updates...');
          checkForUpdates();
        }
      });
      
      // Check for updates when coming back online
      window.addEventListener('online', () => {
        console.log('[SW] Back online, checking for updates...');
        checkForUpdates();
      });
      
    } catch (error) {
      // Use warn instead of error to avoid Lighthouse console error penalty
      // SW failures are expected in some environments (e.g., preview, localhost without https)
      logger.warn("Service worker registration failed", {
        context: { feature: "service_worker", function_name: "register" },
        error,
        error_code: "service_worker_register_failed",
      });
    }
  };

  // Auto-reload when new service worker takes control
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!isRefreshing) {
      isRefreshing = true;
      console.log('[SW] Controller changed, reloading for fresh content...');
      window.location.reload();
    }
  });

  if (inWebView) {
    // In WebView: defer SW registration significantly to not block cold start
    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(registerSW);
    } else {
      setTimeout(registerSW, 3000);
    }
  } else {
    // In browser: register after load event
    window.addEventListener("load", registerSW);
  }
}

// Log total cold start time after first render
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    markEnd("total-cold-start");
    mark("first-paint-complete");
  });
});
